/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from 'react';
import { startupCanView } from 'utils/functions';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ROUTE_STARTUP_PATH } from 'routes';

import tools, {
  TOOL_CAP_TABLE, TOOL_FUNDRAISE, TOOL_PERFORMANCE, TOOL_MENTORINGS, TOOL_USERS,
  TOOL_DATA_ROOM, TOOL_SERVICES, TOOL_REPORTING, TOOL_PROFILE, TOOL_MESSAGES,
} from 'constants/tools';

import {
  Home, Wallet, PieChart, BarChart3, MessageSquareText,
  FileText, PuzzleIcon, Settings, PrinterIcon, Landmark,
} from 'lucide-react';

export const captableSubMenu = (literals, startup, captableId) => [
  {
    path: ROUTE_STARTUP_PATH.CAP_TABLE,
    to: ROUTE_STARTUP_PATH.setCapTable(startup.id, captableId),
    name: literals.capTableSummary,
  },
  {
    path: ROUTE_STARTUP_PATH.CAP_TABLE_OPERATIONS,
    to: ROUTE_STARTUP_PATH.setCapTableOperations(startup.id, captableId),
    name: literals.capTableTransactions,
  },
  {
    path: ROUTE_STARTUP_PATH.CAP_TABLE_SHAREHOLDERS,
    to: ROUTE_STARTUP_PATH.setCapTableShareHolders(startup.id, captableId),
    name: literals.capTableShareholders,
  },
];

const useStartupRows = (rolePath, subMenu, handleShowSubmenu) => {
  const user = useSelector(state => state.i18n.user);
  const startup = useSelector(state => state.global.startup);
  const literals = useSelector(state => state.i18n.literals.leftMenu);
  const canView = useCallback(tool => startupCanView(startup, tool), [startup]);

  const [searchParams] = useSearchParams();
  const captableId = searchParams.get('captable') || null;

  const navRows = useMemo(() => (rolePath === 'startup' && startup ? {
    upper: [
      {
        canView: (user?.role?.length >= 1 && user?.role[0] !== 'user') || tools.length !== 1 || [TOOL_MESSAGES].includes(tools),
        path: ROUTE_STARTUP_PATH.HOME,
        to: ROUTE_STARTUP_PATH.setHome(startup.id),
        name: literals.summary,
        icon: Home,
        exact: true,
      },
      {
        canView: startup.relation?.shareholders?.length > 0,
        path: ROUTE_STARTUP_PATH.MY_POSITION,
        to: ROUTE_STARTUP_PATH.setMyPosition(startup.id),
        name: literals.myPosition,
        icon: Wallet,
      },
      {
        canView: canView(TOOL_CAP_TABLE),
        path: ROUTE_STARTUP_PATH.CAP_TABLE,
        name: literals.capTable,
        icon: PieChart,
        to: ROUTE_STARTUP_PATH.setCapTable(startup.id, captableId),
        subMenu: captableSubMenu(literals, startup, captableId),
      },
      {
        canView: canView(TOOL_FUNDRAISE),
        path: ROUTE_STARTUP_PATH.FUNDRAISE,
        to: ROUTE_STARTUP_PATH.setFundraise(startup.id),
        name: literals.fundraise,
        icon: Landmark,
      },
      {
        canView: canView(TOOL_PERFORMANCE),
        path: ROUTE_STARTUP_PATH.PERFORMANCE,
        to: ROUTE_STARTUP_PATH.setPerformance(startup.id),
        name: literals.performance,
        icon: BarChart3,
      },
      {
        canView: canView(TOOL_MENTORINGS),
        path: ROUTE_STARTUP_PATH.MENTORINGS,
        to: ROUTE_STARTUP_PATH.setMentorings(startup.id),
        name: literals.mentorings,
        icon: MessageSquareText,
      },
      {
        canView: canView(TOOL_REPORTING),
        path: ROUTE_STARTUP_PATH.REPORTING,
        to: ROUTE_STARTUP_PATH.setReporting(startup.id),
        name: literals.reporting,
        icon: PrinterIcon,
      },
      {
        canView: canView(TOOL_DATA_ROOM),
        path: ROUTE_STARTUP_PATH.DATA_ROOM,
        to: ROUTE_STARTUP_PATH.setDataRoom(startup.id),
        name: literals.dataRoom,
        icon: FileText,
      },
      {
        canView: canView(TOOL_SERVICES),
        path: ROUTE_STARTUP_PATH.SERVICES,
        to: ROUTE_STARTUP_PATH.setServices(startup.id),
        name: literals.services,
        icon: PuzzleIcon,
      },
    ],
    bottom: [
      {
        canView: canView(TOOL_USERS) || startupCanView(startup, TOOL_PROFILE),
        path: [ROUTE_STARTUP_PATH.USERS_STARTUP, ROUTE_STARTUP_PATH.VIEW_STARTUP, ROUTE_STARTUP_PATH.EDIT_STARTUP],
        name: literals.settings,
        icon: Settings,
        subMenu: [
          {
            hidden: !startupCanView(startup, TOOL_PROFILE),
            path: [ROUTE_STARTUP_PATH.VIEW_STARTUP, ROUTE_STARTUP_PATH.EDIT_STARTUP],
            to: ROUTE_STARTUP_PATH.setViewStartup(startup.id),
            name: literals.profileStartup,
          },
          {
            hidden: !startupCanView(startup, TOOL_USERS),
            path: ROUTE_STARTUP_PATH.USERS_STARTUP,
            to: ROUTE_STARTUP_PATH.setUsersStartup(startup.id),
            name: literals.userAccess,
          },
          {
            hidden: !startupCanView(startup, TOOL_USERS),
            path: ROUTE_STARTUP_PATH.USERS_STARTUP_PERMISSIONS,
            to: ROUTE_STARTUP_PATH.setUsersStartupPermissions(startup.id),
            name: literals.permissions,
          },
        ],
        showSubmenu: subMenu === 'settings',
        handleShowSubmenu: () => handleShowSubmenu('settings'),
      },
    ],
  } : {}), [subMenu, captableId, rolePath, startup, user?.role]);

  return navRows;
};

export default useStartupRows;
